import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { FiSearch, FiDollarSign, FiDownload } from 'react-icons/fi';
import { exportPaymentsCsv, formatDate, formatMoney, isPaid, toDate, userName } from './adminUtils';

const FILTERS = ['All', 'Paid', 'Pending', 'Bookings', 'Product orders'];

export default function AdminPayments() {
  const { payments, businessesById, usersById } = useOutletContext();
  const [filter, setFilter] = useState('All');
  const [businessId, setBusinessId] = useState('');
  const [query, setQuery] = useState('');

  const businessName = (p) => businessesById[p.businessId]?.businessName || p.businessName || 'Unknown business';
  const customer = (p) => (usersById[p.userId] ? userName(usersById[p.userId]) : p.userEmail || '—');

  const q = query.trim().toLowerCase();
  const visible = payments
    .filter(p => {
      if (filter === 'Paid') return isPaid(p);
      if (filter === 'Pending') return !isPaid(p);
      if (filter === 'Bookings') return p.kind === 'booking';
      if (filter === 'Product orders') return p.kind === 'order';
      return true;
    })
    .filter(p => !businessId || p.businessId === businessId)
    .filter(p => !q || `${p.reference} ${businessName(p)} ${customer(p)}`.toLowerCase().includes(q))
    .sort((a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0));

  const paid = visible.filter(isPaid);
  const totals = [
    { label: 'Paid volume', value: formatMoney(paid.reduce((s, p) => s + p.amount, 0)) },
    { label: 'Platform fees', value: formatMoney(paid.reduce((s, p) => s + p.platformFee, 0)) },
    { label: 'Business payouts', value: formatMoney(paid.reduce((s, p) => s + p.businessPayout, 0)) },
    { label: 'Transactions', value: visible.length },
  ];

  const businessOptions = Object.values(businessesById)
    .filter(b => payments.some(p => p.businessId === b.id))
    .sort((a, b) => (a.businessName || '').localeCompare(b.businessName || ''));

  return (
    <div className="admin-section">
      <div className="admin-toolbar">
        <div className="admin-filters">
          {FILTERS.map(f => (
            <button key={f} className={`filter-btn ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>{f}</button>
          ))}
        </div>
        <select className="admin-select" value={businessId} onChange={e => setBusinessId(e.target.value)} aria-label="Filter by business">
          <option value="">All businesses</option>
          {businessOptions.map(b => <option key={b.id} value={b.id}>{b.businessName}</option>)}
        </select>
        <label className="admin-search">
          <FiSearch aria-hidden="true" />
          <input type="search" placeholder="Search reference, business, customer" value={query} onChange={e => setQuery(e.target.value)} />
        </label>
        <button type="button" className="admin-logout-button" onClick={() => exportPaymentsCsv(visible, businessesById, usersById)} disabled={visible.length === 0}>
          <FiDownload aria-hidden="true" /> <span>Export CSV</span>
        </button>
      </div>

      <div className="admin-stat-grid">
        {totals.map(t => (
          <div key={t.label} className="admin-stat-card">
            <span className="admin-stat-label">{t.label}</span>
            <span className="admin-stat-value">{t.value}</span>
          </div>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="empty-state"><FiDollarSign size={40} /><p>No payments in this view.</p></div>
      ) : (
        <div className="admin-panel admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr><th>Date</th><th>Business</th><th>Customer</th><th>Type</th><th>Amount</th><th>Fee</th><th>Payout</th><th>Status</th><th>Reference</th></tr>
            </thead>
            <tbody>
              {visible.map(p => (
                <tr key={`${p.kind}-${p.id}`}>
                  <td>{formatDate(p.createdAt)}</td>
                  <td>{businessName(p)}</td>
                  <td>{customer(p)}</td>
                  <td>{p.kind === 'order' ? 'Product order' : 'Booking'}</td>
                  <td>{formatMoney(p.amount)}</td>
                  <td>{formatMoney(p.platformFee)}</td>
                  <td>{formatMoney(p.businessPayout)}</td>
                  <td><span className={isPaid(p) ? 'badge-verified' : 'badge-unverified'}>{p.status}</span></td>
                  <td className="admin-mono admin-cell-truncate" title={p.reference}>{p.reference || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
